import { DSR_KINDS, DSR_SORT_FIELDS, type DsrKind } from '@sahab/contracts'
import { z } from 'zod'

/** The queue's tabs, in order (Open, the default, is never written to the URL). */
export const DSR_VIEWS = ['open', 'dueSoon', 'overdue', 'closed'] as const
export type DsrView = (typeof DSR_VIEWS)[number]

/** "Due in 7 days". */
export const DUE_SOON_DAYS = 7

export const DSR_PAGE_SIZE = 50

type DsrSortField = (typeof DSR_SORT_FIELDS)[number]
export type DsrSort = DsrSortField | `-${DsrSortField}`

function isDsrSort(value: string): value is DsrSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (DSR_SORT_FIELDS as readonly string[]).includes(field)
}

/** The data-subject request queue in the URL: tab, kind, search, sort and page. */
export const dsrSearchSchema = z.object({
  view: z.enum(DSR_VIEWS).optional().catch(undefined),
  kind: z.enum(DSR_KINDS).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  sort: z.string().refine(isDsrSort).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
})
export type DsrSearch = z.infer<typeof dsrSearchSchema>

export interface DsrListParams {
  view: DsrView
  kind: DsrKind | undefined
  q: string | undefined
  sort: DsrSort | undefined
  page: number
  pageSize: number
}

export function toDsrListQuery(search: DsrSearch): DsrListParams {
  const view = search.view ?? 'open'
  return {
    view,
    kind: search.kind,
    q: search.q || undefined,
    // Open work reads soonest-due first; closed requests newest first.
    sort: search.sort ?? (view === 'closed' ? '-receivedOn' : 'dueOn'),
    page: search.page ?? 1,
    pageSize: DSR_PAGE_SIZE,
  }
}
